import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, CheckCircle } from 'lucide-react';
import { API_CONFIG } from '../../../config/api';

interface Category {
  id: string;
  name: string;
}

interface FooterProps {
  categories: Category[];
}

export default function Footer({ categories }: FooterProps) {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [description, setDescription] = useState(
    'The Nut Shop offers premium nuts, dried fruits, chocolates, and healthy snacks — freshly packed and shipped from Ohio to your door.'
  );
  const [contactInfo, setContactInfo] = useState<{ email?: string; phone?: string; address?: string }>({});

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_CONFIG.API_URL}/site-settings`);
        const data = await res.json();
        if (data.isSuccess) {
          if (data.result.footerDescription) {
            setDescription(data.result.footerDescription);
          }
          setContactInfo({
            email: data.result.contactEmail || '',
            phone: data.result.contactPhone || '',
            address: data.result.contactAddress || '',
          });
        }
      } catch {
        // Use default
      }
    })();
  }, []);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    setSubscribing(true);
    try {
      const res = await fetch(`${API_CONFIG.API_URL}/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), website: '' }),
      });
      if (res.status === 429) {
        toast.error('Too many requests. Please try again later.');
      } else if (res.ok) {
        setSubscribed(true);
        setEmail('');
        toast.success('Subscribed successfully!');
      } else {
        const data = await res.json();
        toast.error(data.message || 'Something went wrong');
      }
    } catch {
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <footer className="bg-neutral-200 text-neutral-900 border-t border-neutral-300" aria-label="Site footer">
      <div className="storefront-container py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Column 1: Logo & Description */}
          <div className="space-y-4">
            <Link
              to="/"
              className="hover:opacity-80 transition-opacity duration-300 inline-block"
              aria-label="Go to homepage"
            >
              <img 
                src="/thenutshop-logo-y.svg" 
                alt="The Nut Shop" 
                className="h-32 w-auto"
              />
            </Link>
            <p className="text-sm text-neutral-600 leading-relaxed">
              {description}
            </p>
            {(contactInfo.address || contactInfo.phone || contactInfo.email) && (
              <div className="space-y-1.5 text-sm text-neutral-600">
                {contactInfo.address && (
                  <p>{contactInfo.address}</p>
                )}
                {contactInfo.phone && (
                  <p>
                    <a href={`tel:${contactInfo.phone}`} className="hover:text-brand-primary transition-colors">
                      {contactInfo.phone}
                    </a>
                  </p>
                )}
                {contactInfo.email && (
                  <p>
                    <a href={`mailto:${contactInfo.email}`} className="hover:text-brand-primary transition-colors">
                      {contactInfo.email}
                    </a>
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Column 2: Categories */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-neutral-900">Categories</h3>
            <ul className="space-y-2">
              {categories.map((category) => (
                <li key={category.id}>
                  <Link
                    to={`/category/${category.id}`}
                    className="text-sm text-neutral-600 hover:text-brand-primary transition-colors"
                  >
                    {category.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Quick Links */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-neutral-900">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/about"
                  className="text-sm text-neutral-600 hover:text-brand-primary transition-colors"
                >
                  About Us
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="text-sm text-neutral-600 hover:text-brand-primary transition-colors"
                >
                  Contact
                </Link>
              </li>
              <li>
                <Link
                  to="/faq"
                  className="text-sm text-neutral-600 hover:text-brand-primary transition-colors"
                >
                  FAQ
                </Link>
              </li>
              <li>
                <Link
                  to="/returns"
                  className="text-sm text-neutral-600 hover:text-brand-primary transition-colors"
                >
                  Returns & Refunds
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 4: Newsletter & CTA */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-neutral-900">Stay Updated</h3>
            <p className="text-sm text-neutral-600">
              Subscribe to our newsletter for exclusive deals and new products.
            </p>
            {subscribed ? (
              <div className="flex items-center gap-2 text-green-700 bg-green-50 rounded-md px-4 py-3">
                <CheckCircle className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm">You're subscribed! Thanks for joining.</span>
              </div>
            ) : (
              <form onSubmit={handleSubscribe} className="space-y-3">
                {/* Honeypot — hidden from humans, bots fill it */}
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  className="absolute opacity-0 h-0 w-0 pointer-events-none"
                  aria-hidden="true"
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  disabled={subscribing}
                  className="w-full px-4 py-2 rounded-md bg-white border border-neutral-300 text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-brand-primary transition-all"
                />
                <button type="submit" disabled={subscribing} className="w-full storefront-btn-primary flex items-center justify-center gap-2">
                  {subscribing && <Loader2 className="h-4 w-4 animate-spin" />}
                  {subscribing ? 'Subscribing...' : 'Subscribe'}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-neutral-300">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-neutral-500">
              © {currentYear} The Nut Shop. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link
                to="/privacy"
                className="text-sm text-neutral-500 hover:text-brand-primary transition-colors"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                className="text-sm text-neutral-500 hover:text-brand-primary transition-colors"
              >
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
